
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchManyToManyFunc,
	makeJoinEqualsFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserCreditBureauEntity from 'Models/Security/Acl/UserCreditBureauEntity';
import VisitorsCreditBureauEntity from 'Models/Security/Acl/VisitorsCreditBureauEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import { gql } from '@apollo/client';
import { store } from 'Models/Store';
import { computed, runInAction } from 'mobx';

export interface ICreditBureauEntityAttributes extends IModelAttributes {
	name: string;
	humanReadableName: string;

	organisations: Array<
		| Models.OrganisationAuthorisedCreditBureau
		| Models.IOrganisationAuthorisedCreditBureauAttributes
	>;

}

@entity('CreditBureauEntity', 'Credit Bureau')
export default class CreditBureauEntity extends Model
	implements ICreditBureauEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserCreditBureauEntity(),
		new VisitorsCreditBureauEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	/**
	 * Name
	 */
	@Validators.Required()
	@Validators.Unique()
	@observable
	@attribute<CreditBureauEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	

	
	/**
	 * Human Readable version of name
	 */
	@observable
	@attribute<CreditBureauEntity, string>()
	@CRUD({
		name: 'Human Readable Name',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public humanReadableName: string;
	

	/**
	 * Credit bureaus the organisation has agreed to share data with
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Organisation',
		displayType: 'reference-multicombobox',
		order: 30,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.OrganisationAuthorisedCreditBureau,
		optionEqualFunc: makeJoinEqualsFunc('organisationId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'creditBureauEntity',
			oppositeEntityName: 'organisationEntity',
			relationName: 'authorisedCreditBureau',
			relationOppositeName: 'organisation',
			entity: () => Models.CreditBureauEntity,
			joinEntity: () => Models.OrganisationAuthorisedCreditBureau,
			oppositeEntity: () => Models.OrganisationEntity,
		}),
		
	})
	public organisations: Models.OrganisationAuthorisedCreditBureau[] = [];



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ICreditBureauEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ICreditBureauEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.humanReadableName !== undefined) {
				this.humanReadableName = attributes.humanReadableName;
			}
			if (attributes.organisations !== undefined && Array.isArray(attributes.organisations)) {
				for (const model of attributes.organisations) {
					if (model instanceof Models.OrganisationAuthorisedCreditBureau) {
						this.organisations.push(model);
					} else {
						this.organisations.push(new Models.OrganisationAuthorisedCreditBureau(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		organisations {
			${Models.OrganisationAuthorisedCreditBureau.getAttributes().join('\n')}
			organisation {
				${Models.OrganisationEntity.getAttributes().join('\n')}
				${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
			}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			organisations: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'organisations',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}

	// We need credit bureaus in a lot of places throughout the app, and it's unlikely to change between requests,
	// so we use the store to cache the value to prevent fetching it more than once
	@observable private static _creditBureaus = [] as CreditBureauEntity[];
	@observable private static _promise = null as Promise<CreditBureauEntity[]>|null;
	public static creditBureaus = async (): Promise<CreditBureauEntity[]> => {
		if (CreditBureauEntity._promise) {
			return CreditBureauEntity._promise;
		}

		CreditBureauEntity._promise = new Promise(resolve => {
			store.apolloClient.query({
				query: gql`
					query {
						creditBureauEntitys (orderBy: [{path: "name"}]) {
							id
							name
						}
					}
				`,
				fetchPolicy: 'no-cache',
			}).then(({ data }) => {
				runInAction(() => {
					CreditBureauEntity._creditBureaus = data.creditBureauEntitys
						.map((bureau: ICreditBureauEntityAttributes) => new CreditBureauEntity(bureau));

					resolve(CreditBureauEntity._creditBureaus);
				});
			});
		});

		return CreditBureauEntity._promise;
	}

	// If we want to get the credit bureaus, but can't wait, then this will return the current value
	// It will still fetch the credit bureaus, but you won't see the results until a future call
	// If called from within an observer, though, it'll still rerender because this is a computed field
	@computed
	public static get creditBureausSync() {
		CreditBureauEntity.creditBureaus();
		return CreditBureauEntity._creditBureaus;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(CreditBureauEntity.prototype, 'created');
CRUD(modifiedAttr)(CreditBureauEntity.prototype, 'modified');
